/**
 * procedureLines.js
 * Conversões entre as linhas de procedimento editadas na tela
 * ({ procedure_id, minutes, price }) e o formato da API
 * ({ procedure_id, duration "HH:MM:SS", price }).
 * Por convênio a linha leva também o item da tabela da operadora
 * (insurance_procedure_id, insurance_code, insurance_name, insurance_price).
 *
 * Usado pelo AppointmentDrawer (criar) e pelo AppointmentDetailPanel (editar).
 */

import {
  durationToMinutes,
  minutesToDuration,
  sumProcedures,
} from '../../utils/appointmentUtils';

/** Linha da tela a partir do cadastro do procedimento (CompanyConfig.Procedures). */
export function lineFromProcedure(proc) {
  return {
    procedure_id: String(proc.Id),
    minutes: durationToMinutes(proc.Duration),
    price: proc.Price != null && proc.Price !== '' ? String(proc.Price) : '',
  };
}

/**
 * Linhas da tela a partir de um agendamento: as `procedures` gravadas ou, nos
 * agendamentos antigos, o procedimento principal com a duração e o valor dele.
 * Procedimentos que não existem mais no cadastro ficam de fora.
 *
 * @param {object} appointment
 * @param {Array<{Id}>} procedures - cadastro (CompanyConfig.Procedures)
 */
export function linesFromAppointment(appointment, procedures) {
  const known = new Set((procedures || []).map(p => String(p.Id)));
  const stored = Array.isArray(appointment?.procedures) ? appointment.procedures : [];
  const source = stored.length > 0
    ? stored
    : (appointment?.procedure
      ? [{ procedure_id: appointment.procedure, duration: appointment.duration, price: appointment.price }]
      : []);
  return source
    .filter(line => known.has(String(line.procedure_id)))
    .map(line => ({
      procedure_id: String(line.procedure_id),
      minutes: durationToMinutes(line.duration),
      price: line.price != null && line.price !== '' ? String(line.price) : '',
      insurance_procedure_id: line.insurance_procedure_id != null ? String(line.insurance_procedure_id) : null,
      insurance_code: line.insurance_code || null,
      insurance_name: line.insurance_name || null,
      insurance_price: line.insurance_price != null ? String(line.insurance_price) : null,
    }));
}

/** Linhas no formato que set/update_appointment.php recebem. */
export function linesToApi(lines) {
  return (lines || []).map(line => ({
    procedure_id: Number(line.procedure_id),
    duration: minutesToDuration(line.minutes),
    price: Math.round((parseFloat(line.price) || 0) * 100) / 100,
    // 0 = "sem código da operadora" escolhido na tela (o backend não sugere outro)
    insurance_procedure_id: line.insurance_none ? 0 : (line.insurance_procedure_id ? Number(line.insurance_procedure_id) : null),
    insurance_code: line.insurance_code || null,
    insurance_name: line.insurance_name || null,
    insurance_price: line.insurance_price != null && line.insurance_price !== '' ? Number(line.insurance_price) : null,
  }));
}

/** Soma do que a operadora paga pelas linhas com item da tabela: { total, count }. */
export function insuranceTotals(lines) {
  return (lines || []).reduce((acc, line) => {
    if (!line.insurance_procedure_id) return acc;
    const value = parseFloat(line.insurance_price);
    return {
      total: acc.total + (Number.isFinite(value) ? value : 0),
      count: acc.count + 1,
    };
  }, { total: 0, count: 0 });
}

/** Soma das linhas da tela: { minutes, price }. */
export function linesTotals(lines) {
  return sumProcedures((lines || []).map(line => ({
    duration: Number(line.minutes) || 0,
    price: parseFloat(line.price) || 0,
  })));
}
